import React, { useCallback, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import moment from "moment";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { ChakraFAIcon } from "@/components/ui/Icon";

/**
 * Date range (and time) picker based on React Bootstrap DateRangePicker
 * with an additional "All Day" Checkbox.
 *
 * @param {props} props see documentation on attributes at the end of this file
 */

const SHDateRangePicker = (props) => {
	const { t } = useTranslation();
	const [dateText, setDateText] = useState(() => t("screenshotHistory.dateToday"));
	const [allDay, setAllDay] = useState(false);
	const allDayRef = useRef(allDay);
	const oldAllDayRef = useRef(allDay);
	const dateRangePickerRef = useRef();

	const handleDateRangePickerShow = (event, picker) => {
		oldAllDayRef.current = allDayRef.current;

		if (!picker.setEndDateOrig) {
			// inject custom handling logic for date changes
			picker.setStartDateOrig = picker.setStartDate;
			picker.setStartDate = (startDate) => {
				picker.setStartDateOrig(startDate);
				updateDateRangePickerSelection(picker);
			};
			picker.setEndDateOrig = picker.setEndDate;
			picker.setEndDate = (endDate) => {
				picker.setEndDateOrig(endDate);
				updateDateRangePickerSelection(picker);
			};
			picker.updateViewOrig = picker.updateView;
			picker.updateView = () => {
				picker.updateViewOrig();
				updateDateRangePickerSelection(picker);
			};
			// inject custom handling logic for callback
			picker.hideOrig = picker.hide;
			picker.hide = (e) => {
				picker.hideOrig(e);
				handleHide();
			};

			// inject checkbox "All Day" into date picker
			const el = picker.container[0].querySelector(".drp-calendar.left .calendar-time");
			const label = document.createElement("label");
			label.className = "SHModal_allDay";
			const input = document.createElement("input");
			input.addEventListener("change", (event) => {
				setAllDay(event.target.checked);
			});
			input.setAttribute("type", "checkbox");
			if (allDayRef.current) {
				input.setAttribute("checked", "checked");
			}
			label.appendChild(input);
			const text = document.createTextNode(t("screenshotHistory.allDay"));
			label.appendChild(text);
			el.parentNode.insertBefore(label, el);

			// inject multi day info box
			const div = document.createElement("div");
			div.className = "SHModal_multiDayInfoBox";
			div.innerText = t("screenshotHistory.multiDayInfo");
			el.parentNode.insertBefore(div, el);

			// set classname for datepicker DOM element
			picker.container[0].classList.add(props.className);
		}

		if (props.onShow) {
			props.onShow();
		}
	};

	const updateDateRangePickerSelection = useCallback(
		(picker) => {
			// since this function will also be called from an
			// event handler in an uncontrolled component,
			// we need to access allDay via a ref, so we
			// don't get the stale value from when the event handler was defined in a closure
			if (picker.startDate && picker.endDate) {
				if (
					picker.startDate.date() === picker.endDate.date() &&
					picker.startDate.month() === picker.endDate.month() &&
					picker.startDate.year() === picker.endDate.year()
				) {
					// start and end date are the same
					if (picker.container[0].querySelector(".SHModal_multiDayInfoBox")) {
						picker.container[0].querySelector(".SHModal_multiDayInfoBox").classList.remove("enabled");
					}
					if (allDayRef.current) {
						picker.container
							.find(".drp-selected")
							.html(picker.startDate.format("MMMM DD, YYYY") + picker.locale.separator + t("screenshotHistory.allDay"));
					} else {
						picker.container
							.find(".drp-selected")
							.html(picker.startDate.format("MMMM DD, YYYY (hh:mm A") + picker.locale.separator + picker.endDate.format("hh:mm A)"));
					}
				} else {
					if (picker.container[0].querySelector(".SHModal_multiDayInfoBox")) {
						picker.container[0].querySelector(".SHModal_multiDayInfoBox").classList.add("enabled");
					}

					picker.container
						.find(".drp-selected")
						.html(picker.startDate.format("MMMM DD, YYYY") + picker.locale.separator + picker.endDate.format("MMMM DD, YYYY"));
				}
			}
		},
		[t]
	);

	useEffect(() => {
		allDayRef.current = allDay;

		if (dateRangePickerRef.current) {
			updateDateRangePickerSelection(dateRangePickerRef.current.$picker.data("daterangepicker"));
			if (allDay) {
				dateRangePickerRef.current.$picker
					.data("daterangepicker")
					.container[0].querySelectorAll(".calendar-time")
					.forEach((element) => element.classList.add("disabled"));
			} else {
				dateRangePickerRef.current.$picker
					.data("daterangepicker")
					.container[0].querySelectorAll(".calendar-time")
					.forEach((element) => element.classList.remove("disabled"));
			}
		}
	}, [allDay, updateDateRangePickerSelection]);

	const handleHide = () => {
		if (dateRangePickerRef && dateRangePickerRef.current) {
			const picker = dateRangePickerRef.current.$picker.data("daterangepicker");
			if (
				picker.startDate !== null &&
				picker.endDate !== null &&
				(!picker.startDate.isSame(picker.oldStartDate) ||
					!picker.endDate.isSame(picker.oldEndDate) ||
					!allDayRef.current === oldAllDayRef.current)
			) {
				const dateRangeFormatted = picker.container[0].querySelector(".drp-selected").innerText;

				if (
					allDayRef.current ||
					picker.startDate.date() !== picker.endDate.date() ||
					picker.startDate.month() !== picker.endDate.month() ||
					picker.startDate.year() !== picker.endDate.year()
				) {
					props.onCallback(picker.startDate.clone().startOf("day"), picker.endDate.clone().endOf("day"));
					setDateText(dateRangeFormatted);
				} else {
					props.onCallback(picker.startDate.clone(), picker.endDate.clone());
					setDateText(dateRangeFormatted);
				}
			}
		}
	};

	return (
		<DateRangePicker
			ref={dateRangePickerRef}
			initialSettings={{
				startDate: props.startDate,
				endDate: props.endDate,
				// TODO: This depends on bootstrap-daterangepicker that's an outdated repo and only supports moment, and we can't directly switch to dayjs. We can revisit this when we refresh the entire component in the future.
				// Past 14 days including the end date
				minDate: moment().subtract(13, "days"),
				maxDate: moment(),
				opens: "left",
				locale: {
					format: "MMMM DD, YYYY (hh:mm A)",
					cancelLabel: t("screenshotHistory.datePickerReset"),
				},
				timePicker: true,
			}}
			onShow={handleDateRangePickerShow}
		>
			<div className="SHModal_date-picker">
				<ChakraFAIcon icon={"fa-calendar"} variant="solid" color="neutrals.400" mr={3} />
				{dateText}
				<ChakraFAIcon icon="fa-angle-down" variant="solid" color="neutrals.400" mr={3} />
			</div>
		</DateRangePicker>
	);
};

SHDateRangePicker.propTypes = {
	/**
	 * class name to add to the date picker DOM
	 * element. Can then be used as a reference
	 * for CSS style selectors
	 */
	className: PropTypes.string,
	/**
	 * Child elements the date picker should be
	 * attached to. See DateRangePicker docs
	 */
	children: PropTypes.node,
	/**
	 * Start date that should be preset in the
	 * date picker
	 */
	startDate: PropTypes.object,
	/**
	 * End date that should be preset in the
	 * date picker
	 */
	endDate: PropTypes.object,
	/**
	 * Callback function when the date picker is
	 * shown.
	 */
	onShow: PropTypes.func,
	/**
	 * Callback function when the date picker is
	 * closed. Will only be called if the selected
	 * date range has changed.
	 *
	 * @param {startDate} Moment The start date and time that was selected
	 * @param {endDate} Moment The start date and time that was selected
	 * @param {dateRangeFormatted} string The date range as a formatted string, including the all day option.
	 */
	onCallback: PropTypes.func,
};

export default SHDateRangePicker;
